import { useMemo, useState } from 'react';
import { IconVideo } from '@tabler/icons-react';
import { CompositeSegment } from './types/Segment/CompositeSegment';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { classnames } from '@/libs/utils';
import { getClipThumbnailUrl } from '@/Pages/Sessions/AssetsTable/AssetTableClipThumbnail/ClipPreviewUtils';
import { useSceneThumbnail } from '@/Pages/TranscriptPage/useSceneThumbnail';
import SceneThumbnail from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelineSegments/TimelineScenes/SceneThumbnail';
import { getAllCompositeSegments } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/compositeUtils';

const ClipPlayerThumbnail = () => {
  const { clipData } = useClipsContext();
  const randomSeed = useMemo(() => Math.random(), []);
  const thumbnailUrl = useMemo(() => `${getClipThumbnailUrl(clipData)}?${randomSeed}`, [clipData, randomSeed]);
  const [isFailedToLoadThumbnail, setIsFailedToLoadThumbnail] = useState(false);

  const compositeSegment = getAllCompositeSegments(clipData)[0];

  const { showSceneThumbnail, playerConfig, compositionHeight, compositionWidth, croppedImageUrl } = useSceneThumbnail({
    clip: clipData,
    startTimeInSeconds: clipData.asset_metadata.start
  });

  return isFailedToLoadThumbnail ? (
    <div className="absolute inset-0 flex items-center justify-center bg-slate-200/60">
      <IconVideo className="size-64 text-slate-400" />
    </div>
  ) : showSceneThumbnail ? (
    <SceneThumbnail
      playerConfig={playerConfig}
      videoSize={clipData?.asset_metadata?.size}
      thumbnailFrameToDisplay={0}
      compositeSegment={compositeSegment as CompositeSegment}
      compositionHeight={compositionHeight}
      compositionWidth={compositionWidth}
      parentWidth={'100%'}
      mediaContent={clipData.content}
      croppedImageUrl={croppedImageUrl}
    />
  ) : (
    <div
      className={classnames(
        'relative flex items-center justify-center overflow-hidden rounded-md border-2 border-solid border-slate-400 bg-cover bg-center bg-no-repeat shadow-lg',
        {
          'aspect-square': clipData.asset_metadata.size === 'SQUARE',
          'aspect-[9/16]': clipData.asset_metadata.size === 'PORTRAIT',
          'aspect-video': clipData.asset_metadata.size === 'LANDSCAPE'
        }
      )}
    >
      <img
        data-testid="clip-preview-image"
        className={classnames('transition-all duration-300')}
        src={thumbnailUrl}
        alt="Preview"
        onError={() => {
          setIsFailedToLoadThumbnail(true);
        }}
      />
    </div>
  );
};

export default ClipPlayerThumbnail;
